import Close20 from '@carbon/icons-react/es/close/20';
import Save20 from '@carbon/icons-react/es/save/20';
import Search24 from '@carbon/icons-react/es/search/24';
import Settings16 from '@carbon/icons-react/es/settings/16';
import { SecureRoute } from '@okta/okta-react';
import { ConfigProvider, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Switch, useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { FieldSectionType } from 'api/field-sections/types';
import { Field } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { TableSkeleton } from 'components/atoms/table';
import {
  Title,
  TitleDescription,
  TitleWrapper,
} from 'components/organisms/module-settings/key-events-and-intervals/settings/styles';
import { DefaultEmptyKeyEventsAndIntervalsEmptyTable } from 'components/organisms/template-insight/empty-state';
import { RoutePaths } from 'constant';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { useAllFieldsQuery } from 'queries';
import { useKeyEvents } from 'utils/project-fields/use-project-fields';
import { KeyEventsAndIntervalsChart, ChartTypes } from '../charts';
import { Table } from '../table';
import { useAnalyzeIntervalReport } from '../use-analyze-interval-report';
import { SaveInterval } from './save-interval';
import {
  Wrapper,
  LeftSection,
  RightSection,
  SelectedIntervalsWrapper,
  LeftSectionHeaderWrapper,
  LeftSectionFooter,
  SelectOptionsWrapper,
  Heading,
  Caption,
  Select,
  AtomSelect,
  HeaderAndSelectedIntervalsWrapper,
  Header,
  CardWrapper,
  IntervalAtomWrapper,
  IntervalLabel,
  IntervalAtomSelectWrapper,
  AddIntervalButton,
  AndDiv,
} from './styles';
import { computeChartData } from './utils';

interface IntervalData {
  name: string;
  data: {
    y: number;
    growth: number;
    noOfProjects: number;
  };
}

const groupBy = { monthly: 'MONTH', weekly: 'WEEK' };

export const NewIntervalAtom = ({
  interval,
  removeSelectedInterval,
  intervalLookUp = {},
  history,
  reportPayload,
  setReportPayload,
  selectedIntervals,
  setSelectedIntervals,
}) => {
  const events = useKeyEvents() || [];

  const {
    fieldId,
    metaData: { startKeyEventId, endKeyEventId },
  } = interval;
  const [intervalData, setIntervalData] = useState({
    startKeyEventId: startKeyEventId
      ? {
          label:
            events?.find((e) => e.fieldId === Number(startKeyEventId))
              ?.fieldLabel ?? '',
          value: startKeyEventId,
        }
      : {},
    endKeyEventId: endKeyEventId
      ? {
          label:
            events?.find((e) => e.fieldId === Number(endKeyEventId))
              ?.fieldLabel ?? '',
          value: endKeyEventId,
        }
      : {},
  });

  const handleSelectOnChange = ({ label, value }, keyEvent) => {
    setIntervalData({ ...intervalData, [keyEvent]: { label, value } });
  };

  const showSaveButton =
    intervalData.startKeyEventId.value &&
    intervalData.endKeyEventId.value &&
    !Object.keys(intervalLookUp).includes(
      `${intervalData.startKeyEventId.value}-${intervalData.endKeyEventId.value}`,
    );

  useEffect(() => {
    if (
      intervalData.startKeyEventId.value &&
      intervalData.endKeyEventId.value
    ) {
      const selectedIntervalItem =
        intervalLookUp[
          `${intervalData.startKeyEventId.value}-${intervalData.endKeyEventId.value}`
        ];

      if (selectedIntervalItem) {
        const updatedSelectedIntervals = selectedIntervals.filter(
          (item) => item.fieldId !== fieldId,
        );

        if (
          !updatedSelectedIntervals.find(
            (item) => item.fieldId === selectedIntervalItem.fieldId,
          )
        ) {
          setSelectedIntervals([
            ...updatedSelectedIntervals,
            selectedIntervalItem,
          ]);
        } else {
          setSelectedIntervals([...updatedSelectedIntervals]);
        }

        setReportPayload({
          ...reportPayload,
          [selectedIntervalItem.fieldId]: {
            startKeyEventId: intervalData.startKeyEventId.value,
            endKeyEventId: intervalData.endKeyEventId.value,
          },
        });
      } else {
        setSelectedIntervals(
          selectedIntervals.map((_interval) =>
            _interval.fieldId === fieldId
              ? {
                  ..._interval,
                  metaData: {
                    startKeyEventId: intervalData.startKeyEventId.value,
                    endKeyEventId: intervalData.endKeyEventId.value,
                  },
                }
              : _interval,
          ),
        );
        setReportPayload({
          ...reportPayload,
          [fieldId]: {
            startKeyEventId: intervalData.startKeyEventId.value,
            endKeyEventId: intervalData.endKeyEventId.value,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalData]);

  const fieldData = {
    metaData: {
      startKeyEventId: intervalData.startKeyEventId.value,
      endKeyEventId: intervalData.endKeyEventId.value,
    },
    description: '',
    fieldLabel: `${intervalData.startKeyEventId.label} and ${intervalData.endKeyEventId.label}`,
  };

  return (
    <IntervalAtomWrapper>
      <IntervalLabel>
        {intervalLookUp[
          `${intervalData.startKeyEventId.value}-${intervalData.endKeyEventId.value}`
        ]?.fieldLabel ?? ''}
      </IntervalLabel>
      <IntervalAtomSelectWrapper>
        <AtomSelect
          onChange={(e) => handleSelectOnChange(e, 'startKeyEventId')}
          options={events
            ?.filter(
              (event) => event.fieldId !== intervalData.endKeyEventId.value,
            )
            .map((event) => ({
              label: event?.fieldLabel?.toString() ?? '',
              value: event.fieldId,
            }))}
          placeholder="Select a key event"
          defaultValue={{
            label:
              events?.find((e) => e.fieldId === startKeyEventId)?.fieldLabel ??
              'Select a key event',
            value: startKeyEventId ?? null,
          }}
          open
        />

        <AndDiv>&</AndDiv>

        <AtomSelect
          onChange={(e) => {
            handleSelectOnChange(e, 'endKeyEventId');
          }}
          options={events
            ?.filter(
              (event) => event.fieldId !== intervalData.startKeyEventId.value,
            )
            .map((event) => ({
              label: event?.fieldLabel?.toString() ?? '',
              value: event.fieldId,
            }))}
          placeholder="Select a key event"
          defaultValue={{
            label:
              events?.find((e) => e.fieldId === endKeyEventId)?.fieldLabel ??
              'Select a key event',
            value: endKeyEventId ?? null,
          }}
          open
        />
        <Tooltip title={<FormattedMessage id="remove" />}>
          <Button
            size="small"
            type="text"
            icon={<Close20 />}
            onClick={() => {
              removeSelectedInterval(fieldId);
            }}
          />
        </Tooltip>
        {showSaveButton ? (
          <Tooltip
            title={
              <FormattedMessage id="reports.intervals.analyze.save_as_interval" />
            }
          >
            <Button
              size="small"
              type="text"
              icon={<Save20 />}
              onClick={() => {
                history.push({
                  pathname: '/reports/intervals/save-interval',
                  hash: '#view=analyze',
                  state: fieldData,
                });
              }}
            />
          </Tooltip>
        ) : null}
      </IntervalAtomSelectWrapper>
    </IntervalAtomWrapper>
  );
};

export const AnalyzeView = ({
  filter,
  selectedRange,
  viewInitialized,
  zoomLevel,
}) => {
  const { data: allFields } = useAllFieldsQuery();
  const [selectedItems, setSelectedItems] = useState<(number | string)[]>([]);

  const events = allFields?.filter(
    (fieldItem) =>
      fieldItem.fieldType === 'KEY_EVENTS' &&
      fieldItem?.metaData?.relatedFieldId,
  );

  const intervals =
    allFields?.filter(
      (fieldItem) =>
        fieldItem.fieldType === 'INTERVALS' &&
        fieldItem?.metaData?.relatedFieldId,
    ) ?? [];

  const [localStorage, setLocalStorage] = useLocalStorage('analyzeIntervals', {
    selectedIntervals: [] as any as Field[],
    reportPayload: {},
  });

  const [selectedIntervals, setSelectedIntervals] = useState<Field[]>(
    localStorage.selectedIntervals || [],
  );

  const [reportPayload, setReportPayload] = useState(
    localStorage.reportPayload || {},
  );

  useEffect(() => {
    setLocalStorage({ reportPayload, selectedIntervals });
  }, [reportPayload, selectedIntervals, setLocalStorage]);

  const intervalLookupByKE = intervals?.reduce((acc, curr) => {
    const { metaData } = curr;

    return {
      ...acc,
      [`${metaData?.startKeyEventId}-${metaData?.endKeyEventId}`]: curr,
      [`${metaData?.endKeyEventId}-${metaData?.startKeyEventId}`]: curr,
    };
  }, {});

  const selectRef = useRef<any>(null);

  const handleSelectOnChange = (selectedInterval) => {
    setSelectedItems([...selectedItems, selectedInterval.value]);
    const interval = intervals?.find(
      (i) => i.fieldId === Number(selectedInterval.value),
    );

    if (interval) {
      const { fieldId, metaData } = interval;

      setSelectedIntervals([...selectedIntervals, interval]);

      setReportPayload({
        ...reportPayload,
        [fieldId]: {
          startKeyEventId: metaData?.startKeyEventId,
          endKeyEventId: metaData?.endKeyEventId,
        },
      });

      selectRef?.current?.select?.clearValue();
    }
  };

  const addNewInterval = () => {
    setSelectedItems([...selectedItems, 'new']);
    setSelectedIntervals([
      ...selectedIntervals,
      {
        fieldId: uuid(),
        intervalType: 'new',
        metaData: { startKeyEventId: null },
      } as any as Field,
    ]);
  };

  const removeSelectedInterval = (intervalId) => {
    setSelectedIntervals(
      selectedIntervals.filter((item) => item.fieldId !== intervalId),
    );
    setSelectedItems(selectedItems.filter((item) => item !== intervalId));
    setReportPayload((prevData) => {
      const newData = { ...prevData };

      delete newData[intervalId];

      return newData;
    });
  };

  const analyzeReportData = useAnalyzeIntervalReport(
    selectedRange,
    filter,
    groupBy[zoomLevel],
    viewInitialized,
    { startAndEndKeyEventsList: Object.values(reportPayload) ?? [] },
  );

  const actualAnalyzeData = analyzeReportData[1] ?? [];

  const history = useHistory();
  const onClose = () =>
    history.push({
      pathname: RoutePaths.intervalsReport,
      hash: '#view=analyze',
    });

  const translate = useTranslate();

  const { absoluteValueChartOptions } = computeChartData(
    actualAnalyzeData.map((item) => ({
      ...item,
      intervalId: `${item.startKeyEventId}-${item.endKeyEventId}`,
    })),
    zoomLevel,
    intervalLookupByKE,
    translate,
  );

  const OverviewViewTableColumns = [
    {
      title: '',
      children: [
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_nameOfInterval" />
          ),
          dataIndex: 'nameOfInterval',
          fixed: 'left',
          width: 200,
        },
      ],
    },
    {
      title: (
        <FormattedMessage id="reports.intervals.table.column_title.overall" />
      ),
      children: [
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_noOfProjects" />
          ),
          dataIndex: 'noOfProjects',
        },
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_benchmark" />
          ),
          dataIndex: 'benchmark',
        },
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_median" />
          ),
          dataIndex: 'median',
        },
        {
          title: (
            <FormattedMessage id="reports.intervals.table.column_average" />
          ),
          dataIndex: 'average',
        },
      ],
    },
    {
      title:
        zoomLevel === 'monthly' ? (
          <FormattedMessage id="reports.intervals.table.column_monthlyBreakup" />
        ) : (
          <FormattedMessage id="reports.intervals.table.column_weeklyBreakup" />
        ),
      children: absoluteValueChartOptions.xAxis.categories.map((item) => ({
        title: item,
        key: item,
        dataIndex: item,
      })),
    },
  ];

  const tableData = actualAnalyzeData.map((item) => {
    const intervalData = (absoluteValueChartOptions.series.find(
      (item1) =>
        item1.name ===
        intervalLookupByKE[`${item.startKeyEventId}-${item.endKeyEventId}`]
          ?.fieldLabel,
    )?.data ?? []) as any as IntervalData;
    const mappedData = {};

    const { categories } = absoluteValueChartOptions.xAxis;
    let index = 0;

    while (index < categories.length) {
      mappedData[categories[index]] = intervalData[index]
        ? (intervalData[index].y && `${intervalData[index].y}d`) ?? '-'
        : '-';
      index += 1;
    }

    return {
      nameOfInterval:
        intervalLookupByKE[`${item.startKeyEventId}-${item.endKeyEventId}`]
          ?.fieldLabel ??
        `${
          events?.find((event) => event.fieldId === item.startKeyEventId)
            ?.fieldLabel
        } - ${
          events?.find((event) => event.fieldId === item.endKeyEventId)
            ?.fieldLabel
        }`,
      noOfProjects: item.projectIds.length,
      benchmark: item.benchmark ? `${item.benchmark}d` : '-',
      median: item.median ? `${item.median}d` : '-',
      average: item.average ? `${item.average}d` : '-',
      ...mappedData,
    };
  });

  const isLoading = analyzeReportData[0] === 'loading';

  return (
    <>
      <Wrapper>
        <LeftSection>
          <HeaderAndSelectedIntervalsWrapper>
            <LeftSectionHeaderWrapper>
              <Search24 />
              <div>
                <Heading>
                  <FormattedMessage id="reports.intervals.analyze.title" />
                </Heading>
                <Caption>
                  <FormattedMessage id="reports.intervals.analyze.description" />
                </Caption>
              </div>
            </LeftSectionHeaderWrapper>
            <SelectedIntervalsWrapper>
              {selectedIntervals?.map((interval) => (
                <NewIntervalAtom
                  key={interval.fieldId}
                  interval={interval}
                  removeSelectedInterval={removeSelectedInterval}
                  intervalLookUp={intervalLookupByKE}
                  history={history}
                  reportPayload={reportPayload}
                  setReportPayload={setReportPayload}
                  selectedIntervals={selectedIntervals}
                  setSelectedIntervals={setSelectedIntervals}
                />
              ))}
              <SelectOptionsWrapper>
                <AddIntervalButton type="link" onClick={addNewInterval}>
                  <FormattedMessage id="reports.intervals.analyze.add_key_events" />
                </AddIntervalButton>
                <FormattedMessage id="or" />
                <Select
                  ref={selectRef}
                  label="Add an existing interval +"
                  placeholder="Add an existing interval +"
                  onChange={handleSelectOnChange}
                  options={intervals
                    ?.filter(
                      (interval) =>
                        !selectedIntervals
                          .map((_interval) => _interval.fieldId)
                          .includes(interval.fieldId),
                    )
                    .map((interval) => ({
                      label: interval?.fieldLabel?.toString() ?? '',
                      value: interval.fieldId,
                    }))}
                />
              </SelectOptionsWrapper>
            </SelectedIntervalsWrapper>
          </HeaderAndSelectedIntervalsWrapper>
          <LeftSectionFooter>
            <Settings16 />
            <FormattedMessage id="reports.intervals.analyze.manage_key_events_and_intervals" />
            <Link to={RoutePaths.keyeventsAndIntervals}>
              <FormattedMessage id="trailing_here" />
            </Link>
          </LeftSectionFooter>
        </LeftSection>
        <RightSection>
          <KeyEventsAndIntervalsChart
            chartType={ChartTypes.IntervalAsAbsoluteValue}
            chartOptions={absoluteValueChartOptions}
            isLoading={isLoading}
          />

          <CardWrapper>
            {isLoading ? (
              <TableSkeleton columnCount={5} />
            ) : (
              <>
                <Header>
                  <FormattedMessage id="interval_reports.table.header" />
                </Header>
                <ConfigProvider
                  renderEmpty={() =>
                    DefaultEmptyKeyEventsAndIntervalsEmptyTable({
                      noDataDescriptionKey:
                        'reports.intervals.table.listOfIntervals.empty.description',
                    })
                  }
                >
                  <Table
                    columns={OverviewViewTableColumns}
                    dataSource={tableData}
                    pagination={false}
                    scroll={tableData.length > 0 ? { x: 700 } : {}}
                    bordered
                  />
                </ConfigProvider>
              </>
            )}
          </CardWrapper>
        </RightSection>
      </Wrapper>
      <Switch>
        <SecureRoute path="/reports/intervals/save-interval">
          <SaveInterval
            onClose={onClose}
            title={
              <TitleWrapper>
                <Title>
                  <FormattedMessage id="settings.intervals.create.title" />
                </Title>
                <TitleDescription>
                  <FormattedMessage id="settings.intervals.create.description" />
                </TitleDescription>
              </TitleWrapper>
            }
            objectType={FieldSectionType.Project}
          />
        </SecureRoute>
      </Switch>
    </>
  );
};
